import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
  Switch,
  Chip
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import apiClient from '../../networking/axiosconfig';
import { useSelector } from 'react-redux';
import AddBrand from './add';

const Brands = () => {
  const { data: config } = useSelector((state) => state.config);
  const [brands, setBrands] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      // Ensure we're getting the populated categories
      const response = await apiClient.get(`${config.API_BASE_URL}/api/brands`);
      console.log('Fetched brands with categories:', response.data);
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBrand = () => {
    setSelectedBrand(null);
    setIsAddDialogOpen(true);
  };

  const handleEditBrand = (brand) => {
    setSelectedBrand(brand);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (brand) => {
    setBrandToDelete(brand);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!brandToDelete) return;
    
    try {
      await apiClient.delete(`${config.API_BASE_URL}/api/brands/${brandToDelete._id}`);
      setBrands(brands.filter(brand => brand._id !== brandToDelete._id));
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting brand:', error);
    }
  };

  const handleBrandAdded = (newBrand) => {
    setBrands([...brands, newBrand]);
    setIsAddDialogOpen(false);
  };

  const handleBrandUpdated = (updatedBrand) => {
    // Fetch the brand again to ensure we have the latest data with populated categories
    apiClient.get(`${config.API_BASE_URL}/api/brands/${updatedBrand._id}`)
      .then(response => {
        setBrands(brands.map(brand => 
          brand._id === response.data._id ? response.data : brand
        ));
      })
      .catch(error => {
        console.error('Error fetching updated brand:', error);
        // Fallback to the updated brand data we have if the fetch fails
        setBrands(brands.map(brand => 
          brand._id === updatedBrand._id ? updatedBrand : brand
        ));
      });
    setIsEditDialogOpen(false);
  };

  const filteredBrands = brands.filter(brand => 
    brand.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (brand._id && brand._id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Manage Brands</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddBrand}
        >
          Add Brand
        </Button>
      </Box>

      <Box mb={3}>
        <TextField
          label="Search Brands"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name or ID..."
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Icon</TableCell>
              <TableCell>Categories</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredBrands.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No brands found
                </TableCell>
              </TableRow>
            ) : (
              filteredBrands.map((brand) => (
                <TableRow key={brand._id}>
                  <TableCell>{brand.name}</TableCell>
                  <TableCell>
                    {brand.icon && (
                      <img 
                        src={brand.icon} 
                        alt={brand.name} 
                        style={{ width: '50px', height: '50px', objectFit: 'contain' }} 
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {brand.categories && brand.categories.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {brand.categories.map((cat, index) => (
                          <Chip 
                            key={cat._id || index}
                            label={cat.name || 'Unnamed Category'}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">No categories</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEditBrand(brand)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDeleteClick(brand)} 
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Brand Dialog */}
      <AddBrand
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onBrandAdded={handleBrandAdded}
      />

      {/* Edit Brand Dialog */}
      {selectedBrand && (
        <AddBrand
          open={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          onBrandAdded={handleBrandUpdated}
          initialBrand={selectedBrand}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Brand</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the brand "{brandToDelete?.name}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmDelete} 
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Brands;
